import UpdateExtensionLimitForm, {LimitError} from "./UpdateExtensionLimitForm";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {useFormGuard} from "@/utils/formGuard";
import {useBoolState} from "@/hooks";
import Loading from "@/components/breadcrumbs/Loading";
import Section from "@/components/Section";
import {Page} from "@/components/pages";
import {CapabilitySetsWidget} from "../limit-widgets/CapabilitySetsWidget";
import DeviceTypeSetAllocator from "../limit-widgets/DeviceTypeSetAllocator";
import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {Limit, LIMIT_ERROR_MESSAGES} from "../types";
import {Card, Stack, useTheme} from "@mui/material";
import QuotaItemSelector from "@/components/QuotaItemSelector";
import {useAuth} from "@/context/AuthProvider";
import {DataSource, DeviceTypeSetsInfo, useDataSource} from "./DataSource";
import {UserPackageInfo} from "@/api";
import {useEffect, useMemo, useState} from "react";
import {labelForDeviceTypeSet} from "@/utils/translations";
import {userPackageAllocationToQuotaItem} from "../limit-widgets/common";
import {UNLIMITED} from "@/constants";

type Props = {
    extensionId: number;
    domainId: number;
};

const getSetMaxValue = (set: Limit, canAutoIncrementDomainQuota: boolean): number | undefined => {
    let max: number;

    if (canAutoIncrementDomainQuota) {
        max = set.system.limit ?? UNLIMITED;
    } else {
        max = set.domain.allocated;
    }

    return max - set.domain.used;
};

const isCapabilitySetCanBeEnabled = (set: Limit, canAutoIncrementDomainQuota: boolean): boolean => {
    const max = getSetMaxValue(set, canAutoIncrementDomainQuota);
    return max === undefined || max > 0;
};

const isCapabilitySetCanBeDisabled = (set: Limit): boolean => {
    return (set.user.used ?? 0) === 0;
};

const getUserPackageDeviceTypeSets = (
    userPackageName: string,
    userPackages: UserPackageInfo[],
    deviceTypeSetsInfo: DeviceTypeSetsInfo,
): Record<string, number> => {
    const allDeviceTypeSetNames = Object.keys(deviceTypeSetsInfo.domain);

    const userPackageInfoItem = userPackages.find((p) => p.name === userPackageName);

    if (!userPackageInfoItem) return {};

    const result: Record<string, number> = {};

    for (const deviceTypeSetName of allDeviceTypeSetNames) {
        result[deviceTypeSetName] = 0;
    }

    for (const deviceTypeSet of userPackageInfoItem.deviceTypeSets) {
        result[deviceTypeSet.name] = deviceTypeSet.quantity;
    }

    return result;
};

const ExtensionLimitSettings = ({domainId, extensionId}: Props) => {
    const auth = useAuth();
    const dataSource = useDataSource(extensionId, domainId, auth.isSuperuser);

    if (dataSource.anyQueryLoading) return <Loading />;

    return <Content extensionId={extensionId} dataSource={dataSource} />;
};

const Content = ({extensionId, dataSource}: {extensionId: number; dataSource: DataSource}) => {
    const auth = useAuth();
    const [validationErrors, setValidationErrors] = useState<LimitError[]>([]);
    const [enabled, enable, disable] = useBoolState(true);

    const form = new UpdateExtensionLimitForm(extensionId, dataSource, auth.isSuperuser);
    useFormGuard(form.hasChanged);

    useEffect(() => {
        setValidationErrors(form.validate());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.values]);

    async function save() {
        disable();
        await form.submit();
        enable();
    }

    const userLimits = useMemo(() => {
        const result: Record<string, {form: number; current: number}> = {};

        for (const set of form.values.deviceTypeSets) {
            const currentAllocation = dataSource.deviceTypeSetsInfo.userAllocated[set.name];
            result[set.name] = {
                form: set.value,
                current: currentAllocation ?? 0,
            };
        }

        return result;
    }, [dataSource.deviceTypeSetsInfo.userAllocated, form.values.deviceTypeSets]);

    return (
        <Page
            title={defineMessage({
                id: "extensionQuotaSettings.page_title",
                defaultMessage: "Quota settings for user {name}",
            })}
            titleValues={{name: dataSource.limitSettings.users.items[0].displayName}}
        >
            <Stack spacing={2}>
                {dataSource.limitSettings.userPackages.length > 0 && (
                    <FormContainer style={{maxWidth: 600}}>
                        <FormattedFormRow label={defineMessage({id: "user_package", defaultMessage: "User package"})}>
                            <QuotaItemSelector
                                style={{maxWidth: 200}}
                                value={form.values.userPackageName}
                                updateValue={(userPackageName) => form.updateValues({userPackageName})}
                                disabled={!enabled}
                                items={dataSource.limitSettings.domainAllocation.userPackages.map(
                                    userPackageAllocationToQuotaItem,
                                )}
                                variant="outlined"
                                initialValue={dataSource.limitSettings.users.items[0].userPackageName}
                                itemNamePrefix="user_package_names."
                                noErrorForInitialValue
                            />
                        </FormattedFormRow>
                    </FormContainer>
                )}
                <Section
                    label={defineMessage({
                        id: "extensionQuotaSettings.section-title.capability-sets",
                        defaultMessage: "Allowing extra capabilities",
                    })}
                    hidden={dataSource.limitSettings.domainAllocation.capabilitySets.length == 0}
                >
                    <CapabilitySetsWidget
                        disabled={!enabled}
                        setCanBeChecked={(set) =>
                            isCapabilitySetCanBeEnabled(set, auth.hasPermission("auto_increment_domain_quota"))
                        }
                        setCanBeUnchecked={isCapabilitySetCanBeDisabled}
                        value={form.values.capabilitySets}
                        updateValue={(v) => form.updateValues({capabilitySets: v})}
                        sets={form.capabilityLimits}
                        containerStyle={{marginTop: 3, marginBottom: 7}}
                        initialValue={dataSource.limitSettings.userQuotaInfo.capabilitySets}
                        showUsageInfo={true}
                        upCaps={form.capabilitySetsFromUserPackage}
                        compactTable={false}
                        showUsedValues={true}
                    />
                </Section>
                <Section
                    label={defineMessage({
                        id: "extensionQuotaSettings.section-title.device-type-sets",
                        defaultMessage: "Allowing extra devices",
                    })}
                    hidden={dataSource.limitSettings.domainAllocation.deviceTypeSets.length == 0}
                >
                    <DeviceTypeSetAllocator
                        userLimits={userLimits}
                        domainLimits={dataSource.deviceTypeSetsInfo.domain}
                        systemLimits={dataSource.deviceTypeSetsInfo.systemLimit}
                        userPackageDeviceTypeSets={{
                            current: getUserPackageDeviceTypeSets(
                                dataSource.limitSettings.users.items[0].userPackageName,
                                dataSource.limitSettings.userPackages,
                                dataSource.deviceTypeSetsInfo,
                            ),
                            form: getUserPackageDeviceTypeSets(
                                form.values.userPackageName,
                                dataSource.limitSettings.userPackages,
                                dataSource.deviceTypeSetsInfo,
                            ),
                        }}
                        deviceTypeSets={dataSource.limitSettings.deviceTypeSets}
                        disabled={!enabled}
                        updateValue={(name, value) => form.setDeviceTypeSetQuota(name, value)}
                    />
                </Section>
                {validationErrors.length > 0 && <ErrorContainer errors={validationErrors} />}
                <FormContainer style={{maxWidth: 600}}>
                    <FormattedFormRow>
                        <div style={{margin: "1em 0", display: "flex", alignItems: "center"}}>
                            <FormActionButtons
                                mainAction={save}
                                secondaryAction={() => {
                                    form.reset();
                                }}
                                mainButtonEnabled={form.hasChanged && enabled && validationErrors.length === 0}
                                secondaryButtonEnabled={form.hasChanged && enabled}
                            />
                        </div>
                    </FormattedFormRow>
                </FormContainer>
            </Stack>
        </Page>
    );
};

const AgentOrSupervisorError = ({type, groups}: {type: "agent" | "supervisor"; groups: string[]}) => (
    <FormattedMessage {...LIMIT_ERROR_MESSAGES[type]} values={{groups: groups.join(", ")}} />
);

const DevicesNeededError = ({devices}: {devices: {name: string; needed: number}[]}) => {
    const intl = useIntl();

    return (
        <div>
            <FormattedMessage
                id="extensionQuotaSettings.errors.devices_needed"
                defaultMessage="Quotas for device type(s) are less than currently used value:"
            />
            <ul>
                {devices.map((device, index) => (
                    <li key={index}>
                        <FormattedMessage
                            id="extensionQuotaSettings.needed"
                            defaultMessage="{device} (needed: {needed})"
                            values={{
                                device: intl.formatMessage(labelForDeviceTypeSet(device.name)),
                                needed: device.needed,
                            }}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

const ErrorContainer = ({errors}: {errors: LimitError[]}) => {
    console.log("validation errors", errors);
    const theme = useTheme();

    const backgroundColor = theme.palette.error.main;
    const color = theme.palette.error.contrastText;

    return (
        <Card style={{marginTop: 10, padding: 10, backgroundColor}}>
            <ul style={{color}}>
                {errors.map((error, index) => {
                    switch (error.type) {
                        case "agent":
                        case "supervisor":
                            return (
                                <li key={index}>
                                    <AgentOrSupervisorError type={error.type} groups={error.groups} />
                                </li>
                            );
                        case "devices_needed":
                            return (
                                <li key={index}>
                                    <DevicesNeededError devices={error.devices} />
                                </li>
                            );
                    }
                })}
            </ul>
        </Card>
    );
};

export default ExtensionLimitSettings;
